import {ref, readonly, watch} from 'vue';
import {setSpectatorLogLevel} from "@/services/appCommunication.js";

// Log levels enum using Symbol for better type safety
export const LogLevel = Object.freeze({
    NONE: 'none',
    INFO: 'info',
    LOG: 'log',
    WARN: 'warn',
    ERROR: 'error',
    DEBUG: 'debug'
});

// State management
const state = {
    logLevel: ref(import.meta.env.VITE_LOG_LEVEL || LogLevel.INFO),
    defaultLogLevel: ref(import.meta.env.VITE_LOG_LEVEL || LogLevel.INFO),
    environment: ref(import.meta.env.VITE_ENVIRONMENT || 'dev'),
    timerId: ref(null),
    siteInfo: ref({})
};

export function initializeLogging(options = {}) {
    const {token, region, siteInfo} = options;
    const core = window.bgnLogging;

    if (!core) {
        console.error('[LOGGING] logging-core.js not loaded ');
        return {};
    }

    // Set default log level from env
    const envLevel = import.meta.env.VITE_LOG_LEVEL || 'info';
    core.setDefaultLogLevel(envLevel);
    core.setLogLevel(envLevel);

    state.logLevel.value = envLevel
    state.defaultLogLevel.value = envLevel

    const _coreSetLogLevel = core.setLogLevel
    core.setLogLevel = function (level) {
        const res = _coreSetLogLevel(level)
        state.logLevel.value = core.getLogLevel()
        return res
    }

    // Resolve siteInfo whether passed as a Vue ref or plain object
    const resolvedSiteInfo = siteInfo?.value !== undefined ? siteInfo.value : (siteInfo || {});
    core.setSiteInfo({
        ...resolvedSiteInfo,
        environment: import.meta.env.VITE_ENVIRONMENT || 'dev',
    });

    // Keep siteInfo in sync if it was passed as a Vue ref
    if (siteInfo?.value !== undefined) {
        watch(siteInfo, (newVal) => {
            core.setSiteInfo({
                ...newVal,
                environment: import.meta.env.VITE_ENVIRONMENT || 'dev',
            });
        }, {deep: true});
    }

    // Init R7 if available
    if (token) {
        core.initR7(token, region);
    } else {
        console.warn('[LOGGING] No R7 token provided');
    }

    core.setupErrorHandlers();

    return {
        setLogLevel: core.setLogLevel,
        getLogLevel: () => readonly(state.logLevel),
        setErrorLogLevel: core.setErrorLogLevel,
        setSiteInfo: core.setSiteInfo,
    };
}

/**
 * Set the current log level - use from logging-core.js
 * @param {string} level - The log level to set
 * @returns {string} - The current log level
 */
export function setLogLevel(level) {
    setSpectatorLogLevel(level)
    return window.bgnLogging?.setLogLevel(level);
}

/**
 * Get the current log level - use from logging-core.js
 * @returns {string} - the current log level
 */
export function getLogLevel() {
    return window.bgnLogging?.getLogLevel() ?? state.logLevel.value;
}

/**
 * Vue plugin for logging
 * Usage: app.use(LoggingPlugin, options)
 */
export const LoggingPlugin = {
    install(app, options = {}) {
        const logger = initializeLogging(options);

        // Make logging service available globally
        app.config.globalProperties.$logger = logger;

        // Also provide it via provide/inject
        app.provide('logger', logger);

        // Expose log levels
        app.config.globalProperties.$logLevels = LogLevel;
        app.provide('logLevels', LogLevel);
    }
};

// Export a default object that can be used directly with app.use()
export default LoggingPlugin;